import useSWR from "swr";
import { useMemo } from "react";

import axios, { fetcher, endpoints } from "src/utils/axios";

import { ISportProviderCard } from "src/types/sportprovider";
import {
  IFullTransactionCreate,
  ITransaction,
  ITransactionItem,
} from "src/types/transaction";
import { ITransactionSubpayment } from "src/types/order";
import { IReservation } from "src/types/reservation";

// ----------------------------------------------------------------------

export function useGetCartTransactions() {
  const URL = endpoints.transaction.cartlist;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      transactions: (data as ITransaction[]) || null,
      transactionsLoading: isLoading,
      transactionsError: error,
      transactionsValidating: isValidating,
      transactionsEmpty: !isLoading && !data?.length,
    }),
    [data?.transactions, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// export function useGetActiveCartTransaction(transactionId: number) {
//   const URL = endpoints.transaction.details + "/" + transactionId;
//   const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
//   const memoizedValue = useMemo(
//     () => ({
//       transaction: data, //(data as (ITransactionItem & IReservation)[]) || [],
//       transactionLoading: isLoading,
//       transactionError: error,
//       transactionValidating: isValidating,
//       transactionEmpty: !isLoading && !data?.length,
//     }),
//     [data?.transaction, error, isLoading, isValidating]
//   );

//   return memoizedValue;
// }

export function useGetActiveCartTransaction(
  transactionId?: number | null,
  enabled: boolean = true
) {
  const shouldFetch = Boolean(transactionId) && enabled;
  const URL = shouldFetch
    ? `${endpoints.transaction.details}/${transactionId}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    transaction: data ?? null,
    transactionLoading: shouldFetch ? isLoading : false,
    transactionError: shouldFetch ? error : undefined,
    transactionValidating: shouldFetch ? isValidating : false,
    // only “empty” when we actually tried fetching
    transactionEmpty: shouldFetch && !isLoading && !data && !error,
    mutate,
    shouldFetch, // expose for callers if useful
  };
  // return useMemo(
  //   () => ({
  //     transaction: data ?? null,
  //     transactionLoading: isLoading,
  //     transactionError: error,
  //     transactionValidating: isValidating,
  //     transactionEmpty: !isLoading && !data,
  //     mutate,
  //   }),
  //   [data, error, isLoading, isValidating, mutate]
  // );
}

// export function useGetActiveCartTransaction(transactionId: number) {
//   const shouldFetch = transactionId > 0; // Only fetch if transactionId is valid
//   const URL = shouldFetch
//     ? `${endpoints.transaction.details}/${transactionId}`
//     : null;

//   const { data, isLoading, error, isValidating, mutate } = useSWR(
//     shouldFetch ? URL : null,
//     fetcher
//   );

//   const memoizedValue = useMemo(
//     () => ({
//       transaction: data || null,
//       transactionLoading: isLoading,
//       transactionError: error,
//       transactionValidating: isValidating,
//       transactionEmpty: !isLoading && !data,
//       mutate,
//     }),
//     [data, error, isLoading, isValidating, mutate]
//   );

//   return memoizedValue;
// }

export function useGetTransactionItemList(
  providerId: number | null,
  type: string | null,
  month: string,
  enabled: boolean = true
) {
  const shouldFetch =
    enabled && !!providerId && !!type && type !== "select_from_the_list";

  const URL = shouldFetch
    ? `${endpoints.transaction.list.items}/${providerId}/${type}/${month}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      transactions: data || [],
      transactionsLoading: isLoading,
      transactionsError: error,
      transactionsValidating: isValidating,
      transactionsEmpty: !isLoading && !data?.length,
      refetchTransactionList: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetPlayerTransactionItemList(
  type: string | null,
  enabled: boolean = true
) {
  const shouldFetch = enabled && !!type;

  const URL = shouldFetch
    ? `${endpoints.transaction.list.playeritems}/${type}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      transactions: data || [],
      transactionsLoading: isLoading,
      transactionsError: error,
      transactionsValidating: isValidating,
      transactionsEmpty: !isLoading && !data?.length,
      refetchTransactionList: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetActiveCartTransactions() {
  const URL = endpoints.transaction.cartlist;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      transactions: data || [],
      transactionsLoading: isLoading,
      transactionsError: error,
      transactionsValidating: isValidating,
      transactionsEmpty: !isLoading && !data?.length,
      mutate,
    }),
    [data?.transactions, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export async function createTransactionApi(data: Partial<ITransaction>) {
  return await axios.post(endpoints.transaction.create, data);
}

export async function completeTransactionApi(data: {
  billing_id: number | undefined;
  transaction_id: number;
  payment_type: string;
}) {
  return await axios.post(
    endpoints.transaction.complete + "/" + data.transaction_id,
    data
  );
}

export async function addTransactionItemApi(data: IFullTransactionCreate) {
  return await axios.post(endpoints.transaction.item.add, data);
}

export async function updateTransactionItemApi(itemId: number, updates: any) {
  return await axios.post(
    endpoints.transaction.item.update + "/" + itemId,
    updates
  );
}

export async function updateReservationItemApi(reservationitemId: number) {
  return await axios.delete(
    endpoints.transaction.reservationitem + "/" + reservationitemId
  );
}

export async function deleteTransactionItemApi(transactionItemId: number) {
  return await axios.delete(
    endpoints.transaction.item.delete + "/" + transactionItemId
  );
}

export async function deleteCartTransaction(transactionId: number) {
  return await axios.delete(endpoints.transaction.delete + "/" + transactionId);
}

export async function cancelReservations(cancellationData: any) {
  return await axios.post(endpoints.transaction.cancel, cancellationData);
}

export async function refundCanceledReservations(cancellationData: any) {
  return await axios.post(
    endpoints.payment.refund_invoice_creation,
    cancellationData
  );
}

export async function addReservationPeriodItemsToTransaction(data: any) {
  return await axios.post(endpoints.transaction.perioditem.add, data);
}

export async function transformToTrainingApi(reservationId: number, data: any) {
  return await axios.put(
    endpoints.transaction.waitlist + "/" + reservationId,
    data
  );
}

export async function cancelWaitlistReservation(
  reservationId: number,
  data: any
) {
  return await axios.delete(
    endpoints.transaction.waitlist + "/" + reservationId,
    {
      data: data, // PUT the payload here!
    }
  );
}

export async function waitlistApplication(slotId: number) {
  return await axios.post(endpoints.transaction.waitlist + "/" + slotId);
}
